import { useMemo, useRef } from "react";
import { CalendarContainer } from "./components/calendar-container/CalendarContainer";
import { CalendarHeader } from "./components/calendar-header/CalendarHeader";
import { useAllJobEventsQuery } from "./hooks/useAllJobEventsQuery";
import { useCalendarNavigation } from "./hooks/useCalendarNavigation";
import { useJobEventsByIdQuery } from "./hooks/useJobEventsByIdQuery";
import { mappedDataForCalendar } from "./utils/mappedDataForCalendar";
import { CalendarMonthSkeleton } from "./components/CalendarMonthSkeleton";
import { useJobSocketRefetch } from "../../jobs/jobs-list/hooks/useJobSocketRefetch";
import { getCompanyMonthRange } from "../utils/jobOverlapsRange";
import { useAppSelector } from "../../../redux/hooks/redux";
import { selectCurrentCompany } from "../../../redux/features/user/selectors/userState.selectors";

export const JobsCalendar = ({ jobId }) => {
  const calendarRef = useRef(null);
  const companyTimeZone = useAppSelector(selectCurrentCompany)?.timeZone || "UTC";

  const { currentWeek, viewType, handlePrev, handleNext, handleToday, handleViewChange } = useCalendarNavigation(calendarRef, companyTimeZone);

  const queryEventsByJob = useJobEventsByIdQuery(jobId);

  const monthRange = useMemo(() => {
    const range = getCompanyMonthRange(currentWeek.toDate(), companyTimeZone);
    return { startDate: range.start, endDate: range.end };
  }, [currentWeek, companyTimeZone]);

  const queryAllJobs = useAllJobEventsQuery({
    ...monthRange,
    enabled: !jobId,
  });

  const eventsRaw = jobId ? queryEventsByJob?.data?.items : queryAllJobs?.data?.data?.items;
  const socketJobItems = useMemo(() => (eventsRaw ?? [])
    .filter(item => item?.job?.id != null)
    .map(item => ({ id: item.job.id })), [eventsRaw]);
  useJobSocketRefetch({
    jobItems: socketJobItems,
    refetch: jobId ? queryEventsByJob.refetch : queryAllJobs.refetch,
  });

  const dataCalendar = eventsRaw?.map(item => mappedDataForCalendar(item, companyTimeZone))?.filter(Boolean);

  const isLoading = jobId ? queryEventsByJob.isLoading : queryAllJobs.isLoading;

  if (isLoading && !dataCalendar) {
    return <CalendarMonthSkeleton />;
  }

  return (
    <>
      <CalendarHeader
        currentWeek={currentWeek}
        viewType={viewType}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onViewChange={handleViewChange}
        events={dataCalendar}
      />

      <CalendarContainer
        eventsFromApi={dataCalendar}
        calendarRef={calendarRef}
        viewType={viewType}
        loading={isLoading}
        isFromJob={!!jobId}
        currentWeek={currentWeek}
        timeZone={companyTimeZone}
      />
    </>
  );
};
